import { useState, type FormEvent } from "react";
import { useNavigate, useLocation, Link } from "react-router";
import {
  Button,
  Callout,
  FormGroup,
  InputGroup,
  Intent,
  Spinner,
} from "@blueprintjs/core";

import { useAuth } from "../contexts/auth-context";
import { isValidWorkEmail, prettifyAuthError } from "../lib/auth";
import { SystemUseNotification } from "../components/system-use-notification";

function policyIssues(pw: string): string[] {
  const issues: string[] = [];
  if (pw.length < 12) issues.push("at least 12 characters");
  if (!/[A-Z]/.test(pw)) issues.push("an uppercase letter");
  if (!/[a-z]/.test(pw)) issues.push("a lowercase letter");
  if (!/[0-9]/.test(pw)) issues.push("a number");
  if (!/[^A-Za-z0-9]/.test(pw)) issues.push("a symbol");
  return issues;
}

export function Login() {
  const { signIn, status, completeNewPassword, pendingChallenge, clearChallenge } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPw, setShowNewPw] = useState(false);
  // CJIS § 5.5.2 explicit-acknowledgment gate (post-2013 practice).
  // Server-side audit event also records the ack as part of sign-in.
  const [acknowledged, setAcknowledged] = useState(false);

  const from = (location.state as { from?: string } | null)?.from ?? "/";
  const resetSuccess = Boolean(
    (location.state as { resetSuccess?: boolean } | null)?.resetSuccess,
  );

  const emailTouched = email.length > 0;
  const emailValid = isValidWorkEmail(email);
  const emailHelper =
    emailTouched && !emailValid
      ? "Enter a valid email address."
      : undefined;

  const doSignIn = async (e: string, p: string) => {
    setError(null);
    setSubmitting(true);
    try {
      const authenticated = await signIn(e, p);
      if (authenticated) {
        navigate(from, { replace: true });
      }
      // else: pendingChallenge was set by the context; render swap happens below
    } catch (err) {
      setError(prettifyAuthError((err as Error).message));
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await doSignIn(email, password);
  };

  if (status === "loading") {
    return (
      <div data-testid="login-loading" style={loadingShellStyle}>
        <Spinner />
      </div>
    );
  }

  return (
    <div data-testid="login" className="ic-auth-surface" style={shellStyle}>
      <div className="ic-auth-card" style={cardStyle}>
        <div style={brandStyle}>
          <span className="ic-auth-logo" role="img" aria-label="IronCase" />
          <div style={wordmarkStyle}>IRONCASE</div>
        </div>

        {!pendingChallenge && (
          <SystemUseNotification
            variant="banner-with-ack"
            acknowledged={acknowledged}
            onAcknowledge={setAcknowledged}
          />
        )}

        {!pendingChallenge && resetSuccess && (
          <Callout
            intent={Intent.SUCCESS}
            icon="tick"
            style={{ marginTop: 16, marginBottom: 0 }}
          >
            Password updated. Sign in with your new password.
          </Callout>
        )}

        {pendingChallenge ? (
          <NewPasswordStep
            challengeEmail={pendingChallenge.email}
            submitting={submitting}
            error={error}
            newPassword={newPassword}
            confirmPassword={confirmPassword}
            showNewPw={showNewPw}
            onTogglePw={() => setShowNewPw((v) => !v)}
            onNewPassword={setNewPassword}
            onConfirmPassword={setConfirmPassword}
            onCancel={() => {
              setNewPassword("");
              setConfirmPassword("");
              setError(null);
              clearChallenge();
            }}
            onSubmit={async (effectiveEmail) => {
              setError(null);
              setSubmitting(true);
              try {
                await completeNewPassword(newPassword, effectiveEmail);
                setNewPassword("");
                setConfirmPassword("");
                navigate(from, { replace: true });
              } catch (err) {
                setError(prettifyAuthError((err as Error).message));
              } finally {
                setSubmitting(false);
              }
            }}
          />
        ) : (
          <>
            <form onSubmit={onSubmit} style={{ marginTop: 24 }}>
              <FormGroup
                label="Email"
                labelFor="email"
                intent={emailHelper ? Intent.DANGER : Intent.NONE}
                helperText={emailHelper}
              >
                <InputGroup
                  id="email"
                  data-testid="login-email"
                  type="email"
                  placeholder="you@your-agency.gov"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  leftIcon="envelope"
                  autoFocus
                  required
                  autoComplete="email"
                  intent={emailHelper ? Intent.DANGER : Intent.NONE}
                />
              </FormGroup>

              <FormGroup label="Password" labelFor="password">
                <InputGroup
                  id="password"
                  data-testid="login-password"
                  type={showPw ? "text" : "password"}
                  placeholder=""
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  leftIcon="lock"
                  required
                  autoComplete="current-password"
                  rightElement={
                    <Button
                      data-testid="login-password-toggle"
                      minimal
                      small
                      icon={showPw ? "eye-off" : "eye-open"}
                      onClick={() => setShowPw((v) => !v)}
                      aria-label={showPw ? "Hide password" : "Show password"}
                    />
                  }
                />
              </FormGroup>

              {error && (
                <Callout
                  intent={Intent.DANGER}
                  icon="error"
                  style={{ marginBottom: 12 }}
                >
                  {error}
                </Callout>
              )}

              <Button
                type="submit"
                data-testid="login-submit"
                intent={Intent.PRIMARY}
                text="Sign in"
                rightIcon="log-in"
                fill
                large
                loading={submitting}
                disabled={!email || !password || !emailValid || !acknowledged}
              />

              {/* The acknowledgement is the one blocker that isn't self-evident:
                  the email and password fields show their own state, but a
                  filled-in form with a dead button and no explanation reads as a
                  bug. Only surfaced once the rest of the form is satisfied, so
                  it never competes with the field-level validation. */}
              {email && password && emailValid && !acknowledged && (
                <div
                  className="ic-auth-submit-hint"
                  data-testid="login-submit-hint"
                >
                  Acknowledge the notice above to enable sign-in.
                </div>
              )}
            </form>

            <div style={{ marginTop: 12, textAlign: "center" }}>
              <Link
                to="/forgot-password"
                data-testid="login-forgot-password"
                style={{
                  fontSize: 12,
                  color: "var(--bp-text-muted)",
                  textDecoration: "none",
                }}
              >
                Forgot password?
              </Link>
            </div>
          </>
        )}

        <SystemUseNotification variant="footer" />
      </div>
    </div>
  );
}

interface NewPasswordStepProps {
  challengeEmail: string;
  submitting: boolean;
  error: string | null;
  newPassword: string;
  confirmPassword: string;
  showNewPw: boolean;
  onTogglePw: () => void;
  onNewPassword: (v: string) => void;
  onConfirmPassword: (v: string) => void;
  onCancel: () => void;
  onSubmit: (effectiveEmail: string) => Promise<void>;
}

function NewPasswordStep({
  challengeEmail,
  submitting,
  error,
  newPassword,
  confirmPassword,
  showNewPw,
  onTogglePw,
  onNewPassword,
  onConfirmPassword,
  onCancel,
  onSubmit,
}: NewPasswordStepProps) {
  const [email, setEmail] = useState(challengeEmail);

  const emailTouched = email.length > 0;
  const emailValid = isValidWorkEmail(email);
  const emailHelper =
    emailTouched && !emailValid
      ? "Enter a valid email address."
      : undefined;

  const policyFailures = newPassword ? policyIssues(newPassword) : [];
  const passwordsMatch = newPassword === confirmPassword;
  const canSubmit =
    emailValid &&
    newPassword.length > 0 &&
    policyFailures.length === 0 &&
    confirmPassword.length > 0 &&
    passwordsMatch;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    await onSubmit(email);
  };

  return (
    <form data-testid="login-new-password-step" onSubmit={handleSubmit} style={{ marginTop: 24 }}>
      <Callout intent={Intent.NONE} style={{ marginBottom: 16 }}>
        First-time sign-in. Choose a new password to continue.
      </Callout>

      <FormGroup
        label="Email"
        labelFor="np-email"
        intent={emailHelper ? Intent.DANGER : Intent.NONE}
        helperText={emailHelper}
      >
        <InputGroup
          id="np-email"
          data-testid="login-new-password-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          leftIcon="envelope"
          required
          autoComplete="email"
          intent={emailHelper ? Intent.DANGER : Intent.NONE}
        />
      </FormGroup>

      <FormGroup label="New password" labelFor="np-new-password">
        <InputGroup
          id="np-new-password"
          data-testid="login-new-password"
          type={showNewPw ? "text" : "password"}
          value={newPassword}
          onChange={(e) => onNewPassword(e.target.value)}
          leftIcon="lock"
          required
          autoComplete="new-password"
          rightElement={
            <Button
              data-testid="login-new-password-toggle"
              minimal
              small
              icon={showNewPw ? "eye-off" : "eye-open"}
              onClick={onTogglePw}
              aria-label={showNewPw ? "Hide password" : "Show password"}
            />
          }
        />
      </FormGroup>
      {newPassword && policyFailures.length > 0 && (
        <div
          style={{
            fontSize: 11,
            color: "var(--bp-text-dim)",
            marginTop: -8,
            marginBottom: 12,
          }}
        >
          Needs {policyFailures.join(", ")}.
        </div>
      )}
      {!newPassword && (
        <div
          style={{
            fontSize: 11,
            color: "var(--bp-text-dim)",
            marginTop: -8,
            marginBottom: 12,
          }}
        >
          At least 12 characters, with upper, lower, number, and symbol.
        </div>
      )}

      <FormGroup
        label="Confirm new password"
        labelFor="np-confirm-password"
        intent={confirmPassword && !passwordsMatch ? Intent.DANGER : Intent.NONE}
        helperText={
          confirmPassword && !passwordsMatch ? "Passwords don't match." : undefined
        }
      >
        <InputGroup
          id="np-confirm-password"
          data-testid="login-confirm-password"
          type={showNewPw ? "text" : "password"}
          value={confirmPassword}
          onChange={(e) => onConfirmPassword(e.target.value)}
          leftIcon="lock"
          required
          autoComplete="new-password"
          intent={confirmPassword && !passwordsMatch ? Intent.DANGER : Intent.NONE}
        />
      </FormGroup>

      {error && (
        <Callout intent={Intent.DANGER} icon="error" style={{ marginBottom: 12 }}>
          {error}
        </Callout>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <Button
          type="button"
          data-testid="login-new-password-cancel"
          minimal
          text="Cancel"
          onClick={onCancel}
          disabled={submitting}
        />
        <Button
          type="submit"
          data-testid="login-new-password-submit"
          intent={Intent.PRIMARY}
          text="Set password & sign in"
          rightIcon="log-in"
          fill
          large
          loading={submitting}
          disabled={!canSubmit}
        />
      </div>
    </form>
  );
}

const shellStyle: React.CSSProperties = {
  minHeight: "100vh",
  display: "grid",
  placeItems: "center",
  background:
    "radial-gradient(circle at 30% 20%, var(--bp-bg-elev-1) 0%, var(--bp-bg) 60%)",
  padding: 24,
};

const loadingShellStyle: React.CSSProperties = {
  minHeight: "100vh",
  display: "grid",
  placeItems: "center",
  background: "var(--bp-bg)",
};

const cardStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 460,
  background: "var(--bp-bg-elev-1)",
  border: "1px solid var(--bp-border)",
  borderRadius: 8,
  padding: 28,
  // Shadow lives in _login.scss (.ic-auth-card) so it can differ per theme —
  // the old inline 0.45-alpha black was tuned for the dark backdrop and read
  // as a smudge in the default light theme. An inline value would also win
  // over the stylesheet and silently defeat that rule.
};

const brandStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 4,
  paddingBottom: 8,
  borderBottom: "1px solid var(--bp-border-soft)",
};

const wordmarkStyle: React.CSSProperties = {
  fontSize: 20,
  fontWeight: 600,
  letterSpacing: "0.18em",
  color: "var(--bp-text)",
  marginBottom: 4,
};

